import { NavLink, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { 
  LayoutDashboard, 
  Users, 
  Upload,
  Package,
  FileSpreadsheet,
  Coins,
  MessageSquareText,
  GraduationCap,
  CalendarClock,
  TrendingUp,
  Settings,
  ClipboardList,
  ChevronLeft,
  Sparkles,
  ArrowLeft,
  Brain,
  Target,
  Zap,
  MessageCircle,
  Link2,
  Handshake
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useState } from 'react';

interface AdminNavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  end?: boolean;
  badge?: number;
  badgeVariant?: 'default' | 'destructive' | 'warning';
}

function AdminNavItem({ to, icon, label, end = false, badge, badgeVariant = 'default' }: AdminNavItemProps) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200',
          isActive
            ? 'bg-primary text-primary-foreground shadow-md shadow-primary/20'
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
        )
      }
    >
      {icon}
      <span className="flex-1">{label}</span>
      {badge !== undefined && badge > 0 && (
        <Badge variant={badgeVariant === 'warning' ? 'secondary' : badgeVariant} className="text-xs px-1.5 py-0.5">
          {badge}
        </Badge>
      )}
    </NavLink>
  );
}

export function AdminSidebar() {
  const { user } = useAuth();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Fetch counts for badges
  const { data: stats } = useQuery({
    queryKey: ['admin-sidebar-stats'],
    queryFn: async () => {
      const currentUserId = (await supabase.rpc('get_current_user_id')).data;
      
      const [
        { count: usersCount },
        { count: reviewNeededCount },
        { count: unresolvedUncertainties },
        { count: unreadChatCount }
      ] = await Promise.all([
        supabase.from('users').select('*', { count: 'exact', head: true }),
        supabase.from('cee_sheets').select('*', { count: 'exact', head: true }).eq('status', 'draft'),
        supabase.from('ai_sheet_uncertainties').select('*', { count: 'exact', head: true }).eq('resolved', false),
        supabase.from('chat_messages').select('*', { count: 'exact', head: true })
          .is('read_at', null)
          .neq('sender_id', currentUserId)
      ]);
      
      return {
        usersCount: usersCount || 0,
        reviewNeededCount: reviewNeededCount || 0,
        unresolvedUncertainties: unresolvedUncertainties || 0,
        unreadChatCount: unreadChatCount || 0
      };
    },
    refetchInterval: 30000
  });

  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-40 w-64 bg-card border-r border-border flex flex-col transition-all duration-200'
      )}
    >
      {/* Header */}
      <div className="h-16 px-4 flex items-center justify-between border-b border-border">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-lg">
            <Sparkles className="h-4 w-4 text-white" />
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-foreground text-sm">Admin Console</span>
            <span className="text-xs text-muted-foreground">CEE Sales</span>
          </div>
        </div>
      </div>

      {/* Back to Sales */}
      <div className="p-3 border-b border-border">
        <NavLink
          to="/"
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Retour à la plateforme</span>
        </NavLink>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {/* Operations */}
        <div className="pb-2">
          <span className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Operations
          </span>
        </div>
        
        <AdminNavItem
          to="/admin"
          icon={<LayoutDashboard className="h-4 w-4" />}
          label="Dashboard"
          end
        />
        
        <AdminNavItem
          to="/admin/users"
          icon={<Users className="h-4 w-4" />}
          label="Utilisateurs & Équipes"
          badge={stats?.usersCount}
        />
        
        <AdminNavItem
          to="/admin/imports"
          icon={<Upload className="h-4 w-4" />}
          label="Imports & Attribution"
        />
        
        <AdminNavItem
          to="/admin/partners"
          icon={<Handshake className="h-4 w-4" />}
          label="Partenaires"
        />

        {/* Catalogue */}
        <div className="pt-4 pb-2">
          <span className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Catalogue
          </span>
        </div>
        
        <AdminNavItem
          to="/admin/products"
          icon={<Package className="h-4 w-4" />}
          label="Produits & Catalogue"
        />
        
        <AdminNavItem
          to="/admin/cee-sheets"
          icon={<FileSpreadsheet className="h-4 w-4" />}
          label="Fiches CEE & IA"
          badge={stats?.reviewNeededCount}
          badgeVariant="warning"
        />

        {/* Configuration */}
        <div className="pt-4 pb-2">
          <span className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Configuration
          </span>
        </div>
        
        <AdminNavItem
          to="/admin/finance-config"
          icon={<Coins className="h-4 w-4" />}
          label="Coûts & Commissions"
        />
        
        <AdminNavItem
          to="/admin/sales-enablement"
          icon={<MessageSquareText className="h-4 w-4" />}
          label="Aides à la vente"
        />
        
        <AdminNavItem
          to="/admin/training"
          icon={<GraduationCap className="h-4 w-4" />}
          label="Formation"
        />

        {/* Supervision */}
        <div className="pt-4 pb-2">
          <span className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Supervision
          </span>
        </div>
        
        <AdminNavItem
          to="/admin/planning"
          icon={<CalendarClock className="h-4 w-4" />}
          label="Planning"
        />
        
        <AdminNavItem
          to="/admin/finance"
          icon={<TrendingUp className="h-4 w-4" />}
          label="Finance"
        />
        
        <AdminNavItem
          to="/admin/objectives"
          icon={<Target className="h-4 w-4" />}
          label="Objectifs"
        />
        
        <AdminNavItem
          to="/admin/chat-support"
          icon={<MessageCircle className="h-4 w-4" />}
          label="Support Chat"
          badge={stats?.unreadChatCount}
          badgeVariant="destructive"
        />
        
        <AdminNavItem
          to="/admin/client-portals"
          icon={<Link2 className="h-4 w-4" />}
          label="Portails Clients"
        />

        {/* Système */}
        <div className="pt-4 pb-2">
          <span className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Système
          </span>
        </div>
        
        <AdminNavItem
          to="/admin/settings"
          icon={<Settings className="h-4 w-4" />}
          label="Paramètres"
        />
        
        <AdminNavItem
          to="/admin/ai-config"
          icon={<Brain className="h-4 w-4" />}
          label="Configuration IA"
        />
        
        <AdminNavItem
          to="/admin/audit"
          icon={<ClipboardList className="h-4 w-4" />}
          label="Audit Log"
        />
        
        <AdminNavItem
          to="/admin/automations"
          icon={<Zap className="h-4 w-4" />}
          label="Automatisations"
        />
      </nav>

      {/* Footer */}
      <div className="p-3 border-t border-border">
        <div className="px-3 py-2 rounded-lg bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/20">
          <p className="text-xs text-muted-foreground">
            Admin
          </p>
          <p className="text-sm font-medium text-foreground truncate">
            {user?.name}
          </p>
        </div>
      </div>
    </aside>
  );
}
